import React, { forwardRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import useAppLanguage from "../../shared/hooks/useAppLanguage";
import KidsLabelEnglish from "./assets/kids-label-en.svg";
import KidsLabelFrench from "./assets/kids-label-fr.svg";
import EditIcon from "./assets/profile-edit-icon.svg";

import {
  ProfileButton,
  AvatarImg,
  AvatarTitle,
  AvatarSubTitle,
  KidsLabelImg,
  AvatarDisplayName,
  ProfileEditImg,
} from "./styles";

/* Styled components */
const PanicAlertDot = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background-color: ${({ theme }) => theme.colours.orange};
  border-radius: 50%;
`;
/* Styled components end */

/**
 * Profile avatar component that can function as a link or button
 * @component
 * @param {Object} props
 */
const AvatarButton = forwardRef(function AvatarButton(
  {
    avatarInfo,
    title,
    subtitle,
    to,
    onClick,
    alt,
    className = "",
    isKids = false,
    isEditMode = false,
    isMenuMode = false,
    inPanic,
    ...rest
  },
  ref
) {
  const [isImageLoadError, setIsImageLoadError] = useState(false);
  const { isAppLanguageFrench } = useAppLanguage();
  const { t: translate } = useTranslation();
  const theme = useTheme();

  const altText = alt ?? avatarInfo?.displayName;

  return (
    <ProfileButton className={`avatar-button ${className}`} to={to} onClick={onClick} {...rest} ref={ref} theme={theme}>
      {avatarInfo?.displayImages?.[0]?.imageUrl && !isImageLoadError ? (
        <AvatarImg
          src={avatarInfo.displayImages[0].imageUrl}
          $isProfileMenuAvatarImg={className.includes("profile-menu-avatar")}
          alt={altText}
          onError={() => setIsImageLoadError(true)}
          className="avatar"
        />
      ) : (
        <AvatarDisplayName className="avatar-placeholder" theme={theme}>
          <span>{altText}</span>
        </AvatarDisplayName>
      )}
      {isEditMode && <ProfileEditImg src={EditIcon} className="edit-icon" alt="" theme={theme} />}
      {title && (
        <AvatarTitle className={`avatar-title ${title.length > 12 ? "overflow" : ""}`} theme={theme}>
          {title}
        </AvatarTitle>
      )}
      {subtitle && <AvatarSubTitle theme={theme}>{subtitle}</AvatarSubTitle>}
      {isKids && (
        <KidsLabelImg src={isAppLanguageFrench ? KidsLabelFrench : KidsLabelEnglish} alt={translate("kids_profile")} />
      )}
      {isMenuMode && inPanic ? <PanicAlertDot /> : <></>}
    </ProfileButton>
  );
});

AvatarButton.propTypes = {
  avatarInfo: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),
  onClick: PropTypes.func,
  alt: PropTypes.string,
  className: PropTypes.string,
  isKids: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

export default AvatarButton;
